import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowIcon, BackIcon, CloseIcon, EyeOffIcon, HomeIcon, OfflineIcon, PlayPauseIcon, RemoteIcon,
} from "./Icons";

// Titre animé mot par mot (CSS, sans bibliothèque)
export function AnimatedTitle({ text }) {
  return (
    <h2 className="page-title">
      {text.split(" ").map((word, i) => (
        <span key={i} className="word" style={{ "--i": i }}>{word}</span>
      ))}
    </h2>
  );
}

export function Loader({ label }) {
  return (
    <div className="loader" role="status" aria-live="polite">
      <div className="loader-ring" />
      <span>{label}</span>
    </div>
  );
}

export function Section({ id, title, aside, index = 0, className = "", children }) {
  return (
    <section id={id} className={`section ${className}`} style={{ "--i": index }}>
      <div className="section-head">
        <h3 className="section-title">{title}</h3>
        {aside}
      </div>
      {children}
    </section>
  );
}

// Badge "masqué aux visiteurs" (interface admin)
export function HiddenBadge() {
  return (
    <span className="badge-hidden" title="Masqué aux visiteurs" aria-label="Masqué aux visiteurs">
      <EyeOffIcon />
    </span>
  );
}

// Carte appareil : icône + libellé + interrupteur, avec états visuels
// status : undefined | "pending" (commande envoyée) | "error" (échec)
export function DeviceToggle({ label, icon, checked, onChange, disabled, dimmed, status, unknown, badge }) {
  const classes = [
    "device",
    checked && "is-on",
    dimmed && "is-disabled",
    disabled && !dimmed && "is-locked",
    status === "pending" && "is-pending",
    status === "error" && "is-error",
    unknown && "is-unknown",
  ].filter(Boolean).join(" ");

  return (
    <label className={classes} aria-busy={status === "pending" || undefined}>
      {icon && <span className="device-icon">{icon}</span>}
      <span className="device-label">
        {label}
        {badge}
      </span>
      <span className="switch">
        <input type="checkbox" role="switch" checked={!!checked} onChange={onChange} disabled={disabled} />
        <span className="slider" />
      </span>
    </label>
  );
}

export function OfflineBanner({ text }) {
  return (
    <div className="banner" role="alert">
      <OfflineIcon />
      <span>{text}</span>
    </div>
  );
}

// Notifications non bloquantes (remplacent les alert())
export function useToasts() {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);
  const show = useCallback((message, kind = "error") => {
    const id = ++idRef.current;
    setToasts((list) => [...list.slice(-2), { id, message, kind }]);
    setTimeout(() => setToasts((list) => list.filter((t) => t.id !== id)), 3800);
  }, []);
  return { toasts, show };
}

export function ToastHost({ toasts }) {
  return createPortal(
    <div className="toast-host" aria-live="polite">
      {toasts.map((t) => (
        <div key={t.id} className={`toast ${t.kind === "error" ? "is-error" : ""}`}>{t.message}</div>
      ))}
    </div>,
    document.body
  );
}

// Suivi visuel des commandes : "pending" pendant l'envoi, "error" brièvement en cas d'échec
export function useActionStatus() {
  const [status, setStatus] = useState({});
  const failed = useRef(false);

  const markError = useCallback(() => { failed.current = true; }, []);

  const track = useCallback(async (key, action) => {
    failed.current = false;
    setStatus((s) => ({ ...s, [key]: "pending" }));
    try {
      await action();
    } finally {
      const result = failed.current ? "error" : null;
      setStatus((s) => {
        const next = { ...s };
        if (result) next[key] = result; else delete next[key];
        return next;
      });
      if (result) {
        setTimeout(() => setStatus((s) => {
          if (s[key] !== "error") return s;
          const next = { ...s };
          delete next[key];
          return next;
        }), 600);
      }
    }
  }, []);

  return { status, track, markError };
}

// Télécommande Fire TV (identique côté visiteur et admin)
export function RemoteModal({ open, onClose, onKey, onNetflix, onDisney, onPrime, busy, labels }) {
  const closeRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Ne dépend que de "open" : le focus n'est pas déplacé à chaque mise à jour des états
  useEffect(() => {
    if (!open) return undefined;
    const onEsc = (e) => { if (e.key === "Escape") onCloseRef.current(); };
    document.addEventListener("keydown", onEsc);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = overflow;
    };
  }, [open]);

  if (!open) return null;

  const key = (code, className, content, aria) => (
    <button type="button" className={`remote-key ${className}`} onClick={() => onKey(code)} disabled={busy} aria-label={aria}>
      {content}
    </button>
  );

  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" role="dialog" aria-modal="true" aria-label="Fire TV" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3><RemoteIcon /> Fire TV</h3>
          <button ref={closeRef} type="button" className="remote-key remote-close" onClick={onClose} aria-label="Fermer">
            <CloseIcon />
          </button>
        </div>

        <div className="dpad">
          {key(19, "up", <ArrowIcon dir="up" />, labels.up)}
          {key(21, "left", <ArrowIcon dir="left" />, labels.left)}
          {key(23, "ok", "OK", "OK")}
          {key(22, "right", <ArrowIcon dir="right" />, labels.right)}
          {key(20, "down", <ArrowIcon dir="down" />, labels.down)}
        </div>

        <div className="remote-row">
          {key(4, "", <BackIcon />, labels.back)}
          {key(3, "", <HomeIcon />, labels.home)}
          {key(85, "", <PlayPauseIcon />, labels.play)}
        </div>

        <div className="remote-apps">
          <button type="button" className="btn remote-app netflix" onClick={onNetflix} disabled={busy}>Netflix</button>
          <button type="button" className="btn remote-app disney" onClick={onDisney} disabled={busy}>Disney+</button>
          <button type="button" className="btn remote-app prime" onClick={onPrime} disabled={busy}>Prime Video</button>
        </div>

        <p className="remote-status" aria-live="polite">{busy ? labels.sending : ""}</p>
      </div>
    </div>,
    document.body
  );
}
